import React, { useEffect, useState, useRef } from "react";
import "./Styles/Popover.css";
import { createPopper } from "@popperjs/core"; // Import Popper.js

/**
 * Popover Component
 * 
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Whether the popover is visible.
 * @param {HTMLElement} props.referenceElement - The element to anchor the popover.
 * @param {function} props.onClose - Callback to close the popover.
 * @param {ReactNode} props.children - Content to render inside the popover.
 */
const Popover = ({ isOpen, referenceElement, children, onClose }) => {
  const popoverRef = useRef(null);
  const [popperInstance, setPopperInstance] = useState(null);

  useEffect(() => {
    if (isOpen && referenceElement && popoverRef.current) {
      // Create Popper.js instance
      const instance = createPopper(referenceElement, popoverRef.current, {
        placement: "bottom",
        modifiers: [
          {
            name: "flip",
            options: {
              enabled: true, // Allow flipping to keep the popover in view
            },
          },
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport", // Prevent overflow outside the viewport
            },
          },
        ],
      });
      setPopperInstance(instance);

      // Function to handle clicks outside the popover
      const handleClickOutside = (event) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(event.target) &&
          referenceElement &&
          !referenceElement.contains(event.target)
        ) {
          if (onClose) {
            onClose(); // Close the popover if onClose is defined
          } else {
            console.error("onClose is not defined"); // Log an error if onClose is missing
          }
        }
      };

      // Attach the event listener
      document.addEventListener("mousedown", handleClickOutside);

      // Cleanup
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        if (instance) instance.destroy();
      };
    }
  }, [isOpen, referenceElement, onClose]);

  // Don't render if popover is not open
  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      style={{
        position: "absolute",
        zIndex: 1000,
        borderRadius: "8px", // Rounded corners
        overflow: "hidden", // Clip content to the container
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", // Subtle shadow for better visibility
      }}
    >
      {children}
    </div>
  );
};

export default Popover;
